import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton, Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useIsMobile from '../hooks/useIsMobile';
import {
  Societe, SocieteInput, LIMITES, normaliserDomaine, erreurDomaine, nombre, creerSociete, majSociete, messageErreur
} from '../services/societesServices';

interface Props {
  open: boolean;
  societe: Societe | null; // null = création
  societes: Societe[]; // pour signaler un domaine déjà pris sans attendre le backend
  onClose: () => void;
  onSaved: (message: string) => void;
}

// Fenêtre de création / modification d'une société. En modification, seuls les champs changés sont envoyés :
// un domaine historique (« contact@x.com ») n'est pas réécrit si on ne touche qu'au nom.
const SocieteDialog: React.FC<Props> = ({ open, societe, societes, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [nom, setNom] = useState('');
  const [mail, setMail] = useState('');
  const [mailTouche, setMailTouche] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erreurServeur, setErreurServeur] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNom(societe ? societe.nom : '');
    setMail(societe ? societe.mail : '');
    setMailTouche(false);
    setSaving(false);
    setErreurServeur(null);
  }, [open, societe]);

  const mailModifie = !societe || mail.trim() !== societe.mail;
  const domaine = normaliserDomaine(mail);
  const erreurMail = mailModifie ? erreurDomaine(mail, societes, societe ? societe.id : null) : null;
  const erreurNom = !nom.trim() ? 'Nom obligatoire' : nom.trim().length > LIMITES.nom ? `${LIMITES.nom} caractères maximum` : null;
  const valide = !erreurNom && !erreurMail;
  const ancienDomaine = societe ? normaliserDomaine(societe.mail) : '';
  const domaineQuitte = !!societe && mailModifie && !erreurMail && domaine !== ancienDomaine && societe.comptes_domaine > 0;

  let aideMail = 'Les clients inscrits avec une adresse sur ce domaine paient par virement.';
  if ((mailTouche || !!societe) && erreurMail) aideMail = erreurMail;
  else if (mailModifie && domaine && domaine !== mail.trim()) aideMail = `Sera enregistré : ${domaine}`;

  const handleSave = async () => {
    setSaving(true);
    setErreurServeur(null);
    try {
      if (societe) {
        const modifs: Partial<SocieteInput> = {};
        if (nom.trim() !== societe.nom) modifs.nom = nom.trim();
        if (mailModifie) modifs.mail = domaine;
        if (Object.keys(modifs).length) await majSociete(societe.id, modifs);
        onSaved(`Société « ${nom.trim()} » enregistrée`);
      } else {
        await creerSociete({ nom: nom.trim(), mail: domaine });
        onSaved(`Société « ${nom.trim()} » créée`);
      }
      onClose();
    } catch (e) {
      console.error('Erreur enregistrement société', e);
      setErreurServeur(messageErreur(e, 'Enregistrement impossible'));
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6 }}>
        {societe ? 'Modifier la société' : 'Nouvelle société'}
        <IconButton aria-label="Fermer" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {erreurServeur && <Alert severity="error">{erreurServeur}</Alert>}

        <TextField
          label="Nom de la société"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          error={!!societe && !!erreurNom}
          helperText={societe && erreurNom ? erreurNom : undefined}
          inputProps={{ maxLength: LIMITES.nom }}
          required
          autoFocus
          fullWidth
        />

        <TextField
          label="Domaine email"
          placeholder="legrand.fr"
          value={mail}
          onChange={(e) => setMail(e.target.value)}
          onBlur={() => setMailTouche(true)}
          error={(mailTouche || !!societe) && !!erreurMail}
          helperText={aideMail}
          required
          fullWidth
        />

        {domaineQuitte && societe && (
          <Alert severity="warning">
            {nombre(
              societe.comptes_domaine,
              `compte sur @${ancienDomaine} ne paiera plus par virement`,
              `comptes sur @${ancienDomaine} ne paieront plus par virement`
            )}.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSave} disabled={!valide || saving}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SocieteDialog;
